import { useMemo } from 'react';
import { useUser } from '../../providers/UserProvider';
import { isOdoEmail, type ChatConfig } from '@odo/shared-ui';
import { Avatar } from '../Avatar';
import odoChatAvatar from '../../assets/icons/odo-chat.svg';
import api from '../../api/config';

export function useDoctorChatConfig(
    otherSideLastSeenAt?: string | null,
    /** fileId → signed URL, from the chat feed. Resolves files embedded in a v2 body. */
    fileUrls?: Record<string, string>
): Partial<ChatConfig> {
    const { user } = useUser();
    return useMemo(
        () => ({
            isCurrentUser: (email: string | undefined) => !isOdoEmail(email),
            renderAvatar: (props: { user: { name: string; email: string }; imageURL?: string }) => {
                let imgURL: string | undefined = props.imageURL;
                if (!imgURL && props.user.email === user?.email && user?.profilePictureUrl) {
                    imgURL = user.profilePictureUrl;
                }
                if (!imgURL && isOdoEmail(props.user.email)) {
                    imgURL = odoChatAvatar;
                }
                return <Avatar user={props.user} imageURL={imgURL} />;
            },
            filePreviewMode: 'preview' as const,
            editingEnabled: true,
            htmlRenderingEnabled: true,
            styleVariant: 'doctor' as const,
            apiBaseUrl: api.defaults.baseURL ?? '',
            getAuthToken: () => localStorage.getItem('odo_login_token'),
            // Files embedded in a v2 body are image/chatFile nodes carrying a
            // fileId, never a URL; the feed signs one URL per file.
            resolveFileUrl: (fileId: string) => fileUrls?.[fileId],
            // ChatInput appends the send-keybind hint to this string, so keep it
            // tight enough that the whole placeholder still reads in the box.
            inputPlaceholder:
                "How can we help? Leave us a message and we'll get back to you within 24 hours. If it is urgent, please call us.",
            // Doctors see whether ODO has read what they sent, the same
            // double-check the ODO side already shows. Driven entirely by
            // `otherSideLastSeenAt` below, which the chat provider was already
            // fetching while this stayed off.
            receiptsEnabled: true,
            otherSideLastSeenAt
        }),
        [user?.email, user?.profilePictureUrl, otherSideLastSeenAt, fileUrls]
    );
}
